import { Timestamp } from "firebase/firestore";
import { PaymentMethod, PaymentMethodConfigurationDetails } from "./paymentMethod.tsx";
import { Shipping } from "./shipping.tsx";
import { Source } from "./source.tsx";
import { CustomerNotification } from "./customer.tsx";

export class PaymentIntent {
    private id: string;
    private amount: number;
    private currency: string;
    private client_secret: string | null | undefined;
    private customer: string | null | undefined;
    private description: string | null | undefined;
    private automatic_payment_methods: AutomaticPaymentMethods | null | undefined;
    private last_payment_error: LastPaymentError | null | undefined;
    private latest_charge: string | null | undefined;
    private metadata: any;
    private next_action: any | null | undefined;
    private payment_method: string | null | undefined;
    private receipt_email: string | null | undefined;
    private setup_future_usage: string | null | undefined;
    private shipping: Shipping | null | undefined;
    private statement_descriptor: string | null | undefined;
    private statement_descriptor_suffix: string | null | undefined;
    private status: string;
    private object: string;
    private amount_capturable: number;
    private amount_details: AmountDetails | null | undefined;
    private amount_received: number;
    private application: string | null | undefined;
    private application_fee_amount: number | null | undefined;
    private canceled_at: Timestamp | null | undefined;
    private cancellation_reason: string | null | undefined;
    private capture_method: string;
    private confirmation_method: string;
    private created: Timestamp;
    private livemode: boolean;
    private on_behalf_of: string | null | undefined;
    private payment_method_configuration_details: PaymentMethodConfigurationDetails | null | undefined;
    private payment_method_options: PaymentMethod | null | undefined;
    private payment_method_types: string[];
    private presentment: PresentmentDetails | null | undefined;
    private processing: Processing | null | undefined;
    private review: string | null | undefined;
    private transfer_data: TransferData | null | undefined;
    private transfer_group: string | null | undefined;

    constructor(id: string, amount: number, currency: string, client_secret: string | null | undefined, customer: string | null | undefined, description: string | null | undefined, automatic_payment_methods: AutomaticPaymentMethods | null | undefined, last_payment_error: LastPaymentError | null | undefined, latest_charge: string | null | undefined, metadata: any, next_action: any | null | undefined, payment_method: string | null | undefined, receipt_email: string | null | undefined, setup_future_usage: string | null | undefined, shipping: Shipping | null | undefined, statement_descriptor: string | null | undefined, statement_descriptor_suffix: string | null | undefined, status: string, object: string, amount_capturable: number, amount_details: AmountDetails | null | undefined, amount_received: number, application: string | null | undefined, application_fee_amount: number | null | undefined, canceled_at: Timestamp | null | undefined, cancellation_reason: string | null | undefined, capture_method: string, confirmation_method: string, created: Timestamp, livemode: boolean, on_behalf_of: string | null | undefined, payment_method_configuration_details: PaymentMethodConfigurationDetails | null | undefined, payment_method_options: PaymentMethod | null | undefined, payment_method_types: string[], presentment: PresentmentDetails | null | undefined, processing: Processing | null | undefined, review: string | null | undefined, transfer_data: TransferData | null | undefined, transfer_group: string | null | undefined) {
        this.setId(id);
        this.setAmount(amount);
        this.setCurrency(currency);
        this.setClientSecret(client_secret);
        this.setCustomer(customer);
        this.setDescription(description);
        this.setAutomaticPaymentMethods(automatic_payment_methods);
        this.setLastPaymentError(last_payment_error);
        this.setLatestCharge(latest_charge);
        this.setMetadata(metadata);
        this.setNextAction(next_action);
        this.setPaymentMethod(payment_method);
        this.setReceiptEmail(receipt_email);
        this.setSetupFutureUsage(setup_future_usage);
        this.setShipping(shipping);
        this.setStatementDescriptor(statement_descriptor);
        this.setStatementDescriptorSuffix(statement_descriptor_suffix);
        this.setStatus(status);
        this.setObject(object);
        this.setAmountCapturable(amount_capturable);
        this.setAmountDetails(amount_details);
        this.setAmountReceived(amount_received);
        this.setApplication(application);
        this.setApplicationFeeAmount(application_fee_amount);
        this.setCanceledAt(canceled_at);
        this.setCancellationReason(cancellation_reason);
        this.setCaptureMethod(capture_method);
        this.setConfirmationMethod(confirmation_method);
        this.setCreated(created);
        this.setLivemode(livemode);
        this.setOnBehalfOf(on_behalf_of);
        this.setPaymentMethodConfigurationDetails(payment_method_configuration_details);
        this.setPaymentMethodOptions(payment_method_options);
        this.setPaymentMethodTypes(payment_method_types);
        this.setPresentment(presentment);
        this.setProcessing(processing);
        this.setReview(review);
        this.setTransferData(transfer_data);
        this.setTransferGroup(transfer_group);
    }

    static fromJson(json:any) {
        return new PaymentIntent(json.id, json.amount, json.currency, json.client_secret, json.customer, json.description, json.automatic_payment_methods !== null && json.automatic_payment_methods !== undefined ? AutomaticPaymentMethods.fromJson(json.automatic_payment_methods) : null, json.last_payment_error !== null && json.last_payment_error !== undefined ? LastPaymentError.fromJson(json.last_payment_error) : null, json.latest_charge, json.metadata, json.next_action, json.payment_method, json.receipt_email, json.setup_future_usage, json.shipping !== null && json.shipping !== undefined ? Shipping.fromJson(json.shipping) : null, json.statement_descriptor, json.statement_descriptor_suffix, json.status, json.object, json.amount_capturable, json.amount_details !== null && json.amount_details !== undefined ? AmountDetails.fromJson(json.amount_details) : null, json.amount_received, json.application, json.application_fee_amount, json.canceled_at !== null && json.canceled_at !== undefined ? new Timestamp(json.canceled_at, 0) : null, json.cancellation_reason, json.capture_method, json.confirmation_method, json.created !== null && json.created !== undefined ? new Timestamp(json.created, 0) : new Timestamp(0,0), json.livemode, json.on_behalf_of, json.payment_method_configuration_details !== null && json.payment_method_configuration_details !== undefined ? PaymentMethodConfigurationDetails.fromJson(json.payment_method_configuration_details) : null, json.payment_method_options !== null && json.payment_method_options !== undefined ? PaymentMethod.fromJson(json.payment_method_options) : null, json.payment_method_types, json.presentment !== null && json.presentment !== undefined ? PresentmentDetails.fromJson(json.presentment) : null, json.processing !== null && json.processing !== undefined ? Processing.fromJson(json.processing) : null, json.review, json.transfer_data !== null && json.transfer_data !== undefined ? TransferData.fromJson(json.transfer_data) : null, json.transfer_group);
    }

    setId(newId:string) {
        this.id = newId;
    }
    setAmount(newAmount:number) {
        this.amount = newAmount;
    }
    setCurrency(newCurrency:string) {
        this.currency = newCurrency;
    }
    setClientSecret(newClientSecret:string | null | undefined) {
        this.client_secret = newClientSecret;
    }
    setCustomer(newCustomer:string | null | undefined) {
        this.customer = newCustomer;
    }
    setDescription(newDescription:string | null | undefined) {
        this.description = newDescription;
    }
    setAutomaticPaymentMethods(newAutomaticPaymentMethods:AutomaticPaymentMethods | null | undefined) {
        this.automatic_payment_methods = newAutomaticPaymentMethods;
    }
    setLastPaymentError(newLastPaymentError:LastPaymentError | null | undefined) {
        this.last_payment_error = newLastPaymentError;
    }
    setLatestCharge(newLatestCharge:string | null | undefined) {
        this.latest_charge = newLatestCharge;
    }
    setMetadata(newMetadata:any) {
        this.metadata = newMetadata;
    }
    setNextAction(newNextAction:any | null | undefined) {
        this.next_action = newNextAction;
    }
    setPaymentMethod(newPaymentMethod:string | null | undefined) {
        this.payment_method = newPaymentMethod;
    }
    setReceiptEmail(newReceiptEmail:string | null | undefined) {
        this.receipt_email = newReceiptEmail;
    }
    setSetupFutureUsage(newSetupFutureUsage:string | null | undefined) {
        this.setup_future_usage = newSetupFutureUsage;
    }
    setShipping(newShipping:Shipping | null | undefined) {
        this.shipping = newShipping;
    }
    setStatementDescriptor(newStatementDescriptor:string | null | undefined) {
        this.statement_descriptor = newStatementDescriptor;
    }
    setStatementDescriptorSuffix(newStatementDescriptorSuffix:string | null | undefined) {
        this.statement_descriptor_suffix = newStatementDescriptorSuffix;
    }
    setStatus(newStatus:string) {
        this.status = newStatus;
    }
    setObject(newObject:string) {
        this.object = newObject;
    }
    setAmountCapturable(newAmountCapturable:number) {
        this.amount_capturable = newAmountCapturable;
    }
    setAmountDetails(newAmountDetails:AmountDetails | null | undefined) {
        this.amount_details = newAmountDetails;
    }
    setAmountReceived(newAmountReceived:number) {
        this.amount_received = newAmountReceived;
    }
    setApplication(newApplication:string | null | undefined) {
        this.application = newApplication;
    }
    setApplicationFeeAmount(newApplicationFeeAmount:number | null | undefined) {
        this.application_fee_amount = newApplicationFeeAmount;
    }
    setCanceledAt(newCanceledAt:Timestamp | null | undefined) {
        this.canceled_at = newCanceledAt;
    }
    setCancellationReason(newCancellationReason:string | null | undefined) {
        this.cancellation_reason = newCancellationReason;
    }
    setCaptureMethod(newCaptureMethod:string) {
        this.capture_method = newCaptureMethod;
    }
    setConfirmationMethod(newConfirmationMethod:string) {
        this.confirmation_method = newConfirmationMethod;
    }
    setCreated(newCreated:Timestamp) {
        this.created = newCreated;
    }
    setLivemode(newLivemode:boolean) {
        this.livemode = newLivemode;
    }
    setOnBehalfOf(newOnBehalfOf:string | null | undefined) {
        this.on_behalf_of = newOnBehalfOf;
    }
    setPaymentMethodConfigurationDetails(newPaymentMethodConfigurationDetails:PaymentMethodConfigurationDetails | null | undefined) {
        this.payment_method_configuration_details = newPaymentMethodConfigurationDetails;
    }
    setPaymentMethodOptions(newPaymentMethodOptions:PaymentMethod | null | undefined) {
        this.payment_method_options = newPaymentMethodOptions;
    }
    setPaymentMethodTypes(newPaymentMethodTypes:string[]) {
        this.payment_method_types = newPaymentMethodTypes;
    }
    setPresentment(newPresentment:PresentmentDetails | null | undefined) {
        this.presentment = newPresentment;
    }
    setProcessing(newProcessing:Processing | null | undefined) {
        this.processing = newProcessing;
    }
    setReview(newReview:string | null | undefined) {
        this.review = newReview;
    }
    setTransferData(newTransferData:TransferData | null | undefined) {
        this.transfer_data = newTransferData;
    }
    setTransferGroup(newTransferGroup:string | null | undefined) {
        this.transfer_group = newTransferGroup;
    }

    getId() {
        return this.id;
    }
    getAmount() {
        return this.amount;
    }
    getCurrency() {
        return this.currency;
    }
    getClientSecret() {
        return this.client_secret;
    }
    getCustomer() {
        return this.customer;
    }
    getDescription() {
        return this.description;
    }
    getAutomaticPaymentMethods() {
        return this.automatic_payment_methods;
    }
    getLastPaymentError() {
        return this.last_payment_error;
    }
    getLatestCharge() {
        return this.latest_charge;
    }
    getMetadata() {
        return this.metadata;
    }
    getNextAction() {
        return this.next_action;
    }        
    getPaymentMethod() {
        return this.payment_method;
    }
    getReceiptEmail() {
        return this.receipt_email;
    }
    getSetupFutureUsage() {
        return this.setup_future_usage;
    }
    getShipping() {
        return this.shipping;
    }
    getStatementDescriptor() {
        return this.statement_descriptor;
    }
    getStatementDescriptorSuffix() {
        return this.statement_descriptor_suffix;
    }
    getStatus() {
        return this.status;
    }
    getObject() {
        return this.object;
    }
    getAmountCapturable() {
        return this.amount_capturable;
    }
    getAmountDetails() {
        return this.amount_details;
    }
    getAmountReceived() {
        return this.amount_received;
    }
    getApplication() {
        return this.application;
    }
    getApplicationFeeAmount() {
        return this.application_fee_amount;
    }
    getCanceledAt() {
        return this.canceled_at;
    }
    getCancellationReason() {
        return this.cancellation_reason;
    }
    getCaptureMethod() {
        return this.capture_method;
    }
    getConfirmationMethod() {
        return this.confirmation_method;
    }
    getCreated() {
        return this.created;
    }
    getLivemode() {
        return this.livemode;
    }
    getOnBehalfOf() {
        return this.on_behalf_of;
    }
    getPaymentMethodConfigurationDetails() {
        return this.payment_method_configuration_details;
    }
    getPaymentMethodOptions() {
        return this.payment_method_options;
    }
    getPaymentMethodTypes() {
        return this.payment_method_types;
    }
    getPresentment() {
        return this.presentment;
    }
    getProcessing() {
        return this.processing;
    }
    getReview() {
        return this.review;
    }
    getTransferData() {
        return this.transfer_data;
    }
    getTransferGroup() {
        return this.transfer_group;
    }
}

export class AutomaticPaymentMethods {
    private allow_redirect: string | null | undefined;
    private enabled: boolean;

    constructor(allow_redirect: string, enabled: boolean) {
        this.setAllowRedirect(allow_redirect);
        this.setEnabled(enabled);
    }

    static fromJson(json:any) {
        return new AutomaticPaymentMethods(json.allow_redirect, json.enabled);
    }

    setAllowRedirect(newAllowRedirect:string) {
        this.allow_redirect = newAllowRedirect;
    }
    setEnabled(newEnabled:boolean) {
        this.enabled = newEnabled;
    }

    getAllowRedirect() {
        return this.allow_redirect;
    }
    getEnabled() {
        return this.enabled;
    }
}

export class LastPaymentError {
    private advice_code: string | null | undefined;
    private charge: string | null | undefined;
    private code: string | null | undefined;
    private decline_code: string | null | undefined;
    private doc_url: string | null | undefined;
    private message: string | null | undefined;
    private network_advice_code: string | null | undefined;
    private network_decline_code: string | null | undefined;
    private param: string | null | undefined;
    private payment_method: PaymentMethod | null | undefined;
    private payment_method_type: string | null | undefined;
    private source: Source | null | undefined;
    private type: string;

    constructor(advice_code: string | null | undefined, charge: string | null | undefined, code: string | null | undefined, decline_code: string | null | undefined, doc_url: string | null | undefined, message: string | null | undefined, network_advice_code: string | null | undefined, network_decline_code: string | null | undefined, param: string | null | undefined, payment_method: PaymentMethod | null | undefined, payment_method_type: string | null | undefined, source: Source | null | undefined, type: string) {
        this.setAdviceCode(advice_code);
        this.setCharge(charge);
        this.setCode(code);
        this.setDeclineCode(decline_code);
        this.setDocUrl(doc_url);
        this.setMessage(message);
        this.setNetworkAdviceCode(network_advice_code);
        this.setNetworkDeclineCode(network_decline_code);
        this.setParam(param);
        this.setPaymentMethod(payment_method);
        this.setPaymentMethodType(payment_method_type);
        this.setSource(source);
        this.setType(type);
    }

    static fromJson(json:any) {
        return new LastPaymentError(json.advice_code, json.charge, json.code, json.decline_code, json.doc_url, json.message, json.network_advice_code, json.network_decline_code, json.param, json.payment_method !== null && json.payment_method !== undefined ? PaymentMethod.fromJson(json.payment_method) : null, json.payment_method_type, json.source, json.type);
    }

    setAdviceCode(newAdviceCode:string | null | undefined) {
        this.advice_code = newAdviceCode;
    }
    setCharge(newCharge:string | null | undefined) {
        this.charge = newCharge;
    }
    setCode(newCode:string | null | undefined) {
        this.code = newCode;
    }
    setDeclineCode(newDeclineCode:string | null | undefined) {
        this.decline_code = newDeclineCode;
    }
    setDocUrl(newDocUrl:string | null | undefined) {
        this.doc_url = newDocUrl;
    }
    setMessage(newMessage:string | null | undefined) {
        this.message = newMessage;
    }
    setNetworkAdviceCode(newNetworkAdviceCode:string | null | undefined) {
        this.network_advice_code = newNetworkAdviceCode;
    }
    setNetworkDeclineCode(newNetworkDeclineCode:string | null | undefined) {
        this.network_decline_code = newNetworkDeclineCode;
    }
    setParam(newParam:string | null | undefined) {
        this.param = newParam;
    }
    setPaymentMethod(newPaymentMethod:PaymentMethod | null | undefined) {
        this.payment_method = newPaymentMethod;
    }
    setPaymentMethodType(newPaymentMethodType:string | null | undefined) {
        this.payment_method_type = newPaymentMethodType;
    }
    setSource(newSource:Source | null | undefined) {
        this.source = newSource;
    }
    setType(newType:string) {
        this.type = newType;
    }

    getAdviceCode() {
        return this.advice_code;
    }
    getCharge() {
        return this.charge;
    }
    getCode() {
        return this.code;
    }
    getDeclineCode() {
        return this.decline_code;
    }
    getDocUrl() {
        return this.doc_url;
    }
    getMessage() {
        return this.message;
    }
    getNetworkAdviceCode() {
        return this.network_advice_code;
    }
    getNetworkDeclineCode() {
        return this.network_decline_code;
    }
    getParam() {
        return this.param;
    }
    getPaymentMethod() {
        return this.payment_method;
    }
    getPaymentMethodType() {
        return this.payment_method_type;
    }
    getSource() {
        return this.source;
    }
    getType() {
        return this.type;
    }
}

export class AmountDetails {
    private tip: Tip;

    constructor(tip: Tip) {
        this.setTip(tip);
    }

    static fromJson(json:any) {
        return new AmountDetails(Tip.fromJson(json.tip));
    }

    setTip(newTip:Tip) {
        this.tip = newTip;
    }

    getTip() {
        return this.tip;
    }
}

export class Tip {
    private amount: number;

    constructor(amount: number) {
        this.setAmount(amount);
    }

    static fromJson(json:any) {
        return new Tip(json.amount);
    }

    setAmount(newAmount:number) {
        this.amount = newAmount;
    }

    getAmount() {
        return this.amount;
    }
}

export class PresentmentDetails {
    private presentment_amount: number;
    private presentment_currency: string;

    constructor(presentment_amount: number, presentment_currency: string) {
        this.setPresentmentAmount(presentment_amount);
        this.setPresentmentCurrency(presentment_currency);
    }

    static fromJson(json:any) {
        return new PresentmentDetails(json.presentment_amount, json.presentment_currency);
    }

    setPresentmentAmount(newPresentmentAmount:number) {
        this.presentment_amount = newPresentmentAmount;
    }
    setPresentmentCurrency(newPresentmentCurrency:string) {
        this.presentment_currency = newPresentmentCurrency;
    }

    getPresentmentAmount() {
        return this.presentment_amount;
    }
    getPresentmentCurrency() {
        return this.presentment_currency;
    }
}

export class Processing {
    private card: ProcessingCard | null | undefined;
    private type: string;

    constructor(card: ProcessingCard | null | undefined, type: string) {
        this.setCard(card);
        this.setType(type);
    }

    static fromJson(json:any) {
        return new Processing(json.card !== null && json.card !== undefined ? ProcessingCard.fromJson(json.card) : null, json.type);
    }

    setCard(newCard:ProcessingCard | null | undefined) {
        this.card = newCard;
    }
    setType(newType:string) {
        this.type = newType;
    }

    getCard() {
        return this.card;
    }
    getType() {
        return this.type;
    }
}

export class ProcessingCard {
    private customer_notification: CustomerNotification | null | undefined

    constructor(customer_notification: CustomerNotification | null | undefined) {
        this.setCustomerNotification(customer_notification);
    }

    static fromJson(json:any) {
        return new ProcessingCard(CustomerNotification.fromJson(json.customer_notification));
    }

    setCustomerNotification(newCustomerNotification:CustomerNotification | null | undefined) {
        this.customer_notification = newCustomerNotification;
    }

    getCustomerNotification() {
        return this.customer_notification;
    }
}

export class TransferData {
    private amount: number | null | undefined;
    private destination: string;

    constructor(amount: number | null | undefined, destination: string) {
        this.setAmount(amount);
        this.setDestination(destination);
    }

    static fromJson(json:any) {
        return new TransferData(json.amount, json.destination);
    }

    setAmount(newAmount:number | null | undefined) {
        this.amount = newAmount;
    }
    setDestination(newDestination:string) {
        this.destination = newDestination;
    }

    getAmount() {
        return this.amount;
    }
    getDestination() {
        return this.destination;
    }
}